import Swal from 'sweetalert2';
import { IDeleteConfirmSwalOptions } from '@/components/customModal/interface/customModal.interface';

const HEAD_ROW_CLASS = 'delete-confirm-swal-headrow';

/** Icon + title on one row (SweetAlert2 stacks them by default). */
function layoutIconTitleRow(popup: HTMLElement) {
  if (popup.querySelector(`.${HEAD_ROW_CLASS}`)) return;

  const icon = popup.querySelector<HTMLElement>('.swal2-icon');
  const title = popup.querySelector<HTMLElement>('.swal2-title');
  if (!icon || !title) return;

  const row = document.createElement('div');
  row.className = HEAD_ROW_CLASS;
  row.style.setProperty('display', 'flex');
  row.style.setProperty('align-items', 'center');
  row.style.setProperty('gap', '0.75rem');
  row.style.setProperty('width', '100%');
  row.style.setProperty('justify-content', 'flex-start');

  icon.parentNode?.insertBefore(row, icon);
  row.appendChild(icon);
  row.appendChild(title);
}

function applyCompactDeleteConfirmStyles(popup: HTMLElement) {
  const container = popup.parentElement;
  container?.style.setProperty('z-index', '1400');

  popup.style.setProperty('width', 'min(22.5rem, calc(100vw - 2rem))');
  popup.style.setProperty('max-width', '22.5rem');
  popup.style.setProperty('padding', '1rem 1.125rem 1rem');
  popup.style.setProperty('border-radius', '12px');
  popup.style.setProperty('text-align', 'left');
  popup.style.setProperty(
    'box-shadow',
    '0 12px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)'
  );

  const title = popup.querySelector<HTMLElement>('.swal2-title');
  if (title) {
    title.style.setProperty('font-size', '0.9375rem');
    title.style.setProperty('font-weight', '600');
    title.style.setProperty('line-height', '1.35');
    title.style.setProperty('margin', '0');
    title.style.setProperty('padding', '0');
    title.style.setProperty('letter-spacing', '-0.01em');
    title.style.setProperty('text-align', 'left');
    title.style.setProperty('flex', '1');
    title.style.setProperty('min-width', '0');
  }

  const html = popup.querySelector<HTMLElement>('.swal2-html-container');
  if (html) {
    html.style.setProperty('font-size', '0.8125rem');
    html.style.setProperty('line-height', '1.45');
    html.style.setProperty('margin', '0.5rem 0 0');
    html.style.setProperty('padding', '0');
    html.style.setProperty('color', 'rgba(15, 23, 42, 0.72)');
    html.style.setProperty('text-align', 'left');
    html.style.setProperty('width', '100%');
  }

  const icon = popup.querySelector<HTMLElement>('.swal2-icon');
  if (icon) {
    const iconBox = '2.5rem';
    icon.style.setProperty('width', iconBox);
    icon.style.setProperty('height', iconBox);
    icon.style.setProperty('min-width', iconBox);
    icon.style.setProperty('min-height', iconBox);
    icon.style.setProperty('margin', '0');
    icon.style.setProperty('flex-shrink', '0');
    icon.style.setProperty('box-sizing', 'border-box');

    const content = icon.querySelector<HTMLElement>('.swal2-icon-content');
    if (content) {
      content.style.setProperty('font-size', '1.375rem');
      content.style.setProperty('line-height', '1');
      content.style.setProperty('font-weight', '700');
    }

    const svg = icon.querySelector<SVGSVGElement>('svg');
    if (svg) {
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.style.setProperty('width', '100%');
      svg.style.setProperty('height', '100%');
      svg.style.setProperty('display', 'block');
    }
  }

  const actions = popup.querySelector<HTMLElement>('.swal2-actions');
  if (actions) {
    actions.style.setProperty('margin', '0.875rem 0 0');
    actions.style.setProperty('gap', '0.25rem');
    actions.style.setProperty('width', '100%');
    actions.style.setProperty('justify-content', 'flex-end');
    actions.style.setProperty('flex-wrap', 'nowrap');
  }

  popup.querySelectorAll<HTMLElement>('.swal2-styled').forEach((btn) => {
    btn.style.setProperty('padding', '0.35rem 0.75rem');
    btn.style.setProperty('font-size', '0.8125rem');
    btn.style.setProperty('font-weight', '500');
    btn.style.setProperty('border-radius', '5px');
    btn.style.setProperty('min-height', '0');
    btn.style.setProperty('line-height', '1.25');
  });

  // Cancel button: no solid fill — primary-colored text, 5px radius.
  const cancelBtn = popup.querySelector<HTMLElement>('.swal2-cancel');
  if (cancelBtn) {
    cancelBtn.style.setProperty('background-color', 'transparent', 'important');
    cancelBtn.style.setProperty('color', '#D92648', 'important');
    cancelBtn.style.setProperty('box-shadow', 'none', 'important');
    cancelBtn.style.setProperty('border-radius', '5px', 'important');
    cancelBtn.style.setProperty('transition', 'background-color 0.15s');

    const enter = () => {
      cancelBtn.style.setProperty(
        'background-color',
        'rgba(217, 38, 72, 0.08)',
        'important'
      );
    };
    const leave = () => {
      cancelBtn.style.setProperty(
        'background-color',
        'transparent',
        'important'
      );
    };
    cancelBtn.addEventListener('mouseenter', enter);
    cancelBtn.addEventListener('mouseleave', leave);
  }
}

export const deleteConfirmSwal = async ({
  title = 'Are you sure?',
  text,
  icon = 'warning',
  confirmButtonText = 'Yes',
  confirmButtonColor = '#d33',
  cancelButtonColor,
}: IDeleteConfirmSwalOptions): Promise<boolean> => {
  const result = await Swal.fire({
    title,
    text,
    icon,
    showCancelButton: true,
    showCloseButton: false,
    confirmButtonColor,
    cancelButtonColor,
    confirmButtonText,
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    // Avoid double layout with ManagerSidebar GlobalStyles (`scrollbar-gutter: stable` on html).
    scrollbarPadding: false,
    width: 380,
    padding: '0.75rem',
    heightAuto: false,
    didOpen: (popup) => {
      layoutIconTitleRow(popup);
      applyCompactDeleteConfirmStyles(popup);
    },
  });

  return result.isConfirmed;
};
